import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';
import { map, retry, switchMap, tap } from 'rxjs/operators';
import { SessionService } from '../../../core/services/session.service';
import { ProductCodeRange } from '../../../shared/models/LeadProducts';
import { PartialInvalidationModel } from '../../../shared/models/PartialInvalidationModel';
import { productDetail, ProductList } from '../../../shared/models/ProductDetailsModel';
import { SubscriptionDetail } from '../../../shared/models/ProductOnboarding';
import { SessionKeys } from '../../../shared/models/SessionKeys';
import { SurveyParams } from '../../../shared/models/survey-params';
import {
  AdviceSummaryModel,
  AnswerType,
  BeneficiaryChild,
  CappingModel, CatchUpPremium,
  InitiateprojectionToolReqObj,
  KnowledgeAnswerViewModel,
  KnowledgeCheckModel,
  KnowledgeFeedbackModel,
  NextNavigationModel,
  RecommendationModel,
  ScreenTypeCode,
  SimulationSessionData,
  SurveyAnswerModel,
  SurveyModel
} from '../../../shared/models/SurveyModel';
import { InitiateSurveyModel, SurveySimulationModel } from '../../../shared/models/SurveySimulationModel';
import { SustainabilityQuestionsViewModel } from '../../../shared/models/SustainabilityQuestionModel';
import { RouteNames } from '../../../shared/routing/app-routing.constants';
import { FactoryhelperService } from '../../../shared/services/factoryHelper.service';
import { HelperService } from '../../../shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class UserSurveyService {
  private readonly apiUrl: string;
  private surveyQuestions = new Array<SurveyModel>();
  private anonymousSurveyQuestions = new Array<SurveyModel>();
  private sustainablityQuestions = new Array<SustainabilityQuestionsViewModel>();
  recommendedProduct: ProductList;
  cappingModel: CappingModel
  // TODO: put it into a enum of static screen eg : StaticScreenQuestionId and StaticScreenSectionId
  KnowledgeFailedQuestionId = 32;
  KnowledgeFailedSectionId = 14;
  KnowledgeFrozenPeriodQuestionId = 35;
  KnowledgeFrozenPeriodSectionId = 17;
  KnowledgeIntroQuestionId = 31;
  KnowledgeIntroSectionId = 13;
  KnowledgeFrozenPeriodNotOverQuestionId = 61;
  KnowledgeFrozenPeriodNotOverSectionId = 24;
  KnowledgeAlreadyPassedQuestionId = 34;
  KnowledgeAlreadyPassedSectionId = 16;
  AdviceIntroQuestionId = 60;
  AdviceIntroSectionId = 25;
  FinancialIntroQuestionId = 8;
  FinancialIntroSectionId = 11;
  RiskIntroQuestionId = 27;
  RiskIntroSectionId = 12;
  onboardingSectionId = 26;
  FinancialCheckMonthlySavings = 14;
  FinancialCheckMonthlyRevenues = 11;
  SurveyStartingSection = 10;
  blockUserConsistencyCheckQuestionId = 26;
  areYouSureConsistencyCheckQuestionId = 25;
  consistencyCheckIContinueQuestionId = 23;
  userConsistencyCheckSectionId = 11;
  knowledgeExplanationSectionId = 13;
  knowledgeExplanationQuestionId = 65;
  BudgetAndFinancialMonthlySavingsQuestionId = 88;
  BudgetAndFinancialMonthlyRevenueQuestionId = 84;
  LTSknowledgeExplanationQuestionId = 103;
  LTSknowledgeExplanationSectionId = 13;
  LTSKnowledgeFrozenPeriodNotOverQuestionId = 108;
  LTSKnowledgeFrozenPeriodNotOverSectionId = 18;
  LTSKnowledgeAlreadyPassedQuestionId = 106;
  LTSKnowledgeAlreadyPassedSectionId = 16;
  NeedSectionId = 10;
  SustainabilitySectionId = 27;
  SustainabilityIntroQuestionId = 134;
  SustainabilityLevelTwoQuestionId = 137;
  isKnowledgeQuestionAccessible = false;
  recommendedProductBranch: number;
  budgetPartialInvalidationList = [19, 20, 21, 22, 24, 25];
  IntroChildDetails = 0;//to be defined
  formulaIntroSectionId = 11;
  childIntroSectionId = 12;
  YOLKnowledgeIntroQuestionId = 157;
  onboardingYongoSectionId = 26;
  knowledgeExperienceFeedbacksectionId = 16;
  knowledgeExperienceFeedbackQuestionId = 167;
  projectionToolUrl: string;

  constructor(
    private readonly factoryhelperService: FactoryhelperService,
    private readonly router: Router,
    private readonly helper: HelperService,
    private readonly session: SessionService,
    private readonly translateService: AgTranslateService
  ) {
    this.apiUrl = AgAppConfigService.get<string>('applicativeApiUrl');
    this.projectionToolUrl = AgAppConfigService.get<string>('projectionToolUrl');
  }

  getSimulation(personId: number, isCompletedNeeded = false): Observable<SurveySimulationModel[]> {
    const isCompleteLoad = isCompletedNeeded;
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/person/${personId}/completeload/${isCompleteLoad}/simulations`;
    return this.factoryhelperService.get<{ Result: SurveySimulationModel[] }>(url).pipe(map(x => x.Result));
  }

  initiateSimulation(productCodeRange: ProductCodeRange, moduleCode: number): Observable<InitiateSurveyModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silparameter/productrange/${productCodeRange}/module/${moduleCode}/startsurvey`;
    return this.factoryhelperService.get<{ Result: InitiateSurveyModel }>(url).pipe(map(x => x.Result));
  }

  createSimulation(data: SurveySimulationModel, productCodeRange: ProductCodeRange, moduleCode: number): Observable<SurveySimulationModel[]> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/savesimulationdata`;
    return this.initiateSimulation(productCodeRange, moduleCode).pipe(
      switchMap((res: InitiateSurveyModel) => {
        if (res) {
          const newData = { ...data };
          newData.Survey = res.SurveyCode;
          newData.SectionId = res.FirstSectionId;
          newData.ProductRange = productCodeRange;
          if (productCodeRange === ProductCodeRange.Yongo) {
            // for yongo flow, utm source name need to be passed to DB while creating quote after coming from external source
            newData.PartnerSource = this.session.getUtmParams()["utm_source"];
          }
          return this.factoryhelperService.post<{ Result: SurveySimulationModel[] }>(url, newData).pipe(tap(_res => {
            if (productCodeRange === ProductCodeRange.Yongo) {
              //utm source is per quote, once used, cannot be used further for any other quote which is not created after coming from a given source
              this.session.clearUtmParams();
            }
          }), map(x => x.Result));
        } else {
          return EMPTY
        }
      })
    )
    //return this.factoryhelperService.post<{ Result: SurveySimulationModel[] }>(url, data).pipe(map(x => x.Result));
  }

  getQuestionsFromAPI(surveyId: number, simulationId: number, sectionId: number): Observable<SurveyModel[]> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/survey/${surveyId}/simulation/${simulationId}/section/${sectionId}/questions`;
    return this.factoryhelperService.get<{ Result: SurveyModel[] }>(url).pipe(map(x => x.Result));
  }

  getAnonymousQuestionsFromAPI(surveyId: number, sectionId: number): Observable<SurveyModel[]> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/survey/${surveyId}/section/${sectionId}/questions`;
    return this.factoryhelperService.get<{ Result: SurveyModel[] }>(url).pipe(map(x => x.Result));
  }

  fetchSustainabilityQuestions(surveyId: number, simulationId: number, sectionId: number): Observable<SustainabilityQuestionsViewModel[]> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/survey/${surveyId}/simulation/${simulationId}/section/${sectionId}/sustainabilityquestions`;
    return this.factoryhelperService.get<{ Result: SustainabilityQuestionsViewModel[] }>(url).pipe(map(x => x.Result));
  }

  invalidateAnswer(quoteDetailsId: number, surveyId: number, sectionId: number, questionId: number, isBulk: boolean): Observable<boolean> {
    const data = {
      quoteDetailsId: quoteDetailsId,
      surveyId: surveyId,
      sectionId: sectionId,
      questionId: questionId,
      isBulk: isBulk
    };
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/quoteDetails/${quoteDetailsId}/survey/${surveyId}/lastsection/${sectionId}/question/${questionId}/isbulk/${isBulk}/invalidateanswers`;
    return this.factoryhelperService.patch<{ Result: boolean }>(url, data).pipe(
      map(x => x.Result),
      retry(2)
    );
  }

  makeSectionInvalid(survey: SurveyParams, currentSection: number): void {
    this.invalidateAnswer(survey.simulationId, survey.surveyId, currentSection, 0, true).subscribe();
  }

  partialInvalidation(simulationId: number, surveyId: number, sectionId: number, questionIds: number[]): Observable<boolean> {
    const data: PartialInvalidationModel = {
      SectionId: sectionId,
      SurveyId: surveyId,
      QuestionId: questionIds
    };
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${simulationId}/partialinvalidateanswers`;
    return this.factoryhelperService.patch<{ Result: boolean }>(url, data).pipe(
      map(x => x.Result),
      retry(2)
    );
  }

  postSurveyAnswer(answerModel: SurveyAnswerModel, ruleEngineName?: string): Observable<NextNavigationModel> {
    const culture = this.translateService.currentLang;
    const apiParams = new HttpParams().set('isRuleEngine', `${ruleEngineName ? true : false}`);
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${answerModel.SimulationId}/question/${answerModel.LeadQuestionId}/savesimulationanswers`;
    return this.factoryhelperService.put<{ Result: NextNavigationModel }>(url, answerModel, { params: apiParams }).pipe(
      map(x => x.Result),
      retry(2)
    );
  }

  saveSustainabilityAnswers(simulationId: number, level1QuestionId: number, branchId: number, answerList: SurveyAnswerModel[]): Observable<NextNavigationModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${simulationId}/${level1QuestionId}/${branchId}/savesustainablilityanswers`;
    return this.factoryhelperService.post<{ Result: NextNavigationModel }>(url, answerList).pipe(
      map(x => x.Result),
      retry(2)
    );
  }

  checkKnowledge(surveyId: number, simulationId: number, sectionId: number): Observable<KnowledgeCheckModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/person/${localStorage.personId}/survey/${surveyId}/lastsection/${sectionId}/simulation/${simulationId}/productrecommendation`;
    return this.factoryhelperService.get<{ Result: KnowledgeCheckModel }>(url).pipe(map(x => x.Result));
  }

  saveKnowledgeTrial(data: KnowledgeAnswerViewModel): Observable<KnowledgeCheckModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/saveknowledgetrial`;
    return this.factoryhelperService.post<{ Result: KnowledgeCheckModel }>(url, data).pipe(map(x => x.Result));
  }

  saveChildDetails(data: BeneficiaryChild, simulationId: number): Observable<unknown> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silproductonboarding/simulation/${simulationId}/savebeneficiarychild`;
    return this.factoryhelperService.put<{ Result: unknown }>(url, data).pipe(map(x => x.Result));
  }

  getRecommendedProducts(data: RecommendationModel): Observable<unknown> {
    const culture = this.translateService.currentLang;
    const queryString = Object.keys(data).map(key => key + '=' + data[key]).join('&');
    const apiParams = new HttpParams({ fromString: queryString });
    const url = `${this.apiUrl}/${culture}/sildecisiontree/scenario/${data.ScenarioId}/getrecommendedproductdetails`;
    return this.factoryhelperService.get<{ Result: unknown }>(url, { params: apiParams }).pipe(map(x => x.Result));
  }

  sendAdviceMail(data: unknown): Observable<boolean> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/communication/sendagadviceemail`;
    return this.factoryhelperService.post<{ Result: boolean }>(url, data).pipe(map(x => x.Result));
  }
  saveSubscriptionDetails(data: SubscriptionDetail): Observable<unknown> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silproductonboarding/savesilsubscriptiondetails`;
    return this.factoryhelperService.post<{ Result: unknown }>(url, data).pipe(map(x => x.Result));
  }
  saveSilChoiceDetails(data: SubscriptionDetail): Observable<unknown> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silproductonboarding/savesilchoicedetails`;
    return this.factoryhelperService.post<{ Result: unknown }>(url, data).pipe(map(x => x.Result));
  }
  deleteSurvey(simulationId: number): Observable<boolean> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcustomerzone/deletependingsimulation/${simulationId}`;
    return this.factoryhelperService.patch<{ Result: boolean }>(url, null).pipe(map(x => x.Result));
  }

  saveQuestionsInBulk(data: Array<SurveyAnswerModel>, simulationid: number): Observable<SurveySimulationModel[]> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${simulationid}/savebulksimulationanswers`;
    return this.factoryhelperService.put<{ Result: SurveySimulationModel[] }>(url, data).pipe(map(x => x.Result));
  }

  getKnowledgeFeedback(personId: number, simulationId: number, surveyCode: number): Observable<KnowledgeFeedbackModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/person/${personId}/simulation/${simulationId}/survey/${surveyCode}/knowledgeandexperiencedetails`;
    return this.factoryhelperService.get<{ Result: KnowledgeFeedbackModel }>(url).pipe(map(x => x.Result));
  }

  getQuestions(): SurveyModel[] {
    return this.surveyQuestions;
  }

  getAnonymousQuestions(): SurveyModel[] {
    return this.anonymousSurveyQuestions;
  }

  get getSustainabilityQuestions(): SustainabilityQuestionsViewModel[] {
    return this.sustainablityQuestions;
  }

  set setSustainabilityQuestions(data: SustainabilityQuestionsViewModel[]) {
    this.sustainablityQuestions = data;
  }

  getSectionSpecificQuestions(sectionId: number): SurveyModel[] {
    return this.surveyQuestions.filter(question => question.SectionId === sectionId);
  }
  setQuestions(questionList: SurveyModel[]): void {
    this.surveyQuestions = [...this.surveyQuestions, ...questionList];
  }
  setAnonymousQuestions(questionList: SurveyModel[]): void {
    this.anonymousSurveyQuestions = questionList;
  }
  clearQuestionCache(): void {
    this.surveyQuestions = [];
  }
  clearAnonymousQuestionCache(): void {
    this.anonymousSurveyQuestions = [];
    this.helper.clearSession(SessionKeys.EMAnswers);
  }

  updateQuestionWithAnswer(questionList: SurveyModel[], isAnonymousModule?: boolean): void {
    isAnonymousModule ? this.anonymousSurveyQuestions = questionList : this.surveyQuestions = questionList;
  }

  getQuestionDetails(id: number, forAnonymous?: boolean): SurveyModel {
    let selectedQuestion: SurveyModel
    if (forAnonymous) {
      selectedQuestion = this.anonymousSurveyQuestions.find(({ QuestionId }) => {
        return QuestionId === id;
      });
    } else {
      selectedQuestion = this.surveyQuestions.find(({ QuestionId }) => {
        return QuestionId === id;
      });
    }
    return selectedQuestion;
  }

  getAnswer(questionId: number, forAnonymous?: boolean): string | number {
    return this.getQuestionDetails(questionId, forAnonymous)?.PreviousAnswerValue;
  }

  checkValidity(value: number, validationName: string): boolean {
    switch (validationName) {
      case 'INPUTINFERIORTO1':
        if (value < 1) {
          return true;
        } else {
          return false;
        }
      case 'INPUTINFERIORTO3':
        if (value < 3) {
          return true;
        } else {
          return false;
        }
      case 'INPUTSUPERIORTO20':
        if (value > 20) {
          return true;
        } else {
          return false;
        }
      default:
        return false;
    }
  }

  public moveToNextQuestion(currentSection: number, questionId: number, sectionId: number, isAnonymousModule?: boolean, surveyId?: number): void {
    // fetching the next auestion details
    const nextQuestionDetails = this.getQuestionDetails(questionId, isAnonymousModule);
    // if next question is available and next section remains same then just doing internal navigation from one question to another
    if (nextQuestionDetails && currentSection === sectionId) {
      this.surveyInternalNavigation(nextQuestionDetails, currentSection);
    }
    // Incase the next section is different from the current one
    if (currentSection !== sectionId) {
      if (!nextQuestionDetails) {
        // Incase there is no question details for the next section, then first calling the api and then doing the navigation
        if (isAnonymousModule) {
          this.fetchAnonymousQuestionsForGivenSection(surveyId, sectionId).subscribe(_RES => {
            // passing the next question detail from the new section after fetching and merging with the previous section
            this.surveyInternalNavigation(this.getQuestionDetails(questionId), sectionId);
          });
        } else {
          this.fetchQuestionsForGivenSection(sectionId).subscribe(_RES => {
            // passing the next question detail from the new section after fetching and merging with the previous section
            this.surveyInternalNavigation(this.getQuestionDetails(questionId), sectionId);
          });
        }
      } else {
        this.surveyInternalNavigation(nextQuestionDetails, sectionId);
      }
    }
  }

  private surveyInternalNavigation(nextQuestionDetails: SurveyModel, currentSection: number) {
    if (nextQuestionDetails) {
      if (nextQuestionDetails.ScreenTypeCode === ScreenTypeCode.StaticScreen) {
        if (nextQuestionDetails.StaticScreenId === RouteNames.intro) {
          void this.router.navigate(['/' + RouteNames.whoAreYou]);
        }
        else {
          void this.router.navigate([`${nextQuestionDetails.SurveyCode}/${nextQuestionDetails.SectionId}/${RouteNames.surveyModule}/${RouteNames.questions}/${nextQuestionDetails.QuestionId}/${nextQuestionDetails.StaticScreenId}`]);
        }
      } else {
        void this.router.navigate([`${nextQuestionDetails.SurveyCode}/${currentSection}/${RouteNames.surveyModule}/${RouteNames.questions}/${nextQuestionDetails.QuestionId}`]);
      }
    }
  }

  fetchQuestionsForGivenSection(sectionId: number): Observable<SurveyModel[]> {
    const surveyParams = JSON.parse(atob(sessionStorage.userSurvey as string)) as SurveyParams;
    return this.getQuestionsFromAPI(surveyParams.surveyId, surveyParams.simulationId, sectionId).pipe(
      tap((res: SurveyModel[]) => {
        if (res.length) {
          this.setQuestions(res);
        }
      })
    );
  }

  fetchAnonymousQuestionsForGivenSection(surveyId: number, sectionId: number): Observable<SurveyModel[]> {
    return this.getAnonymousQuestionsFromAPI(surveyId, sectionId).pipe(
      tap((res: SurveyModel[]) => {
        res?.length && this.setAnonymousQuestions(res);
      })
    );
  }
  saveAnswer(questionId: number, sectionId: number, answer: string | number, screenType: ScreenTypeCode, surveyCode: number, questionTag: string, answerTag?: string, ruleEngineName?: string): Observable<NextNavigationModel> {
    const answerModel: SurveyAnswerModel = {
      SimulationId: Number((JSON.parse(atob(sessionStorage.userSurvey as string)) as SurveyParams).simulationId),
      LeadQuestionId: questionId,
      AnswerType: this.getAnswerType(screenType),
      SectionId: sectionId,
      AnswerValue: answer,
      RuleEngineName: ruleEngineName,
      SurveyCode: surveyCode,
      QuestionTag: questionTag,
      AnswerTag: answerTag
    };
    return this.postSurveyAnswer(answerModel, ruleEngineName);
  }

  public getAnswerType(screenType: ScreenTypeCode): AnswerType {
    switch (screenType) {
      case ScreenTypeCode.YesNo:
      case ScreenTypeCode.RadioButtons:
      case ScreenTypeCode.CardsWithIcons: {
        return AnswerType.LeadContent;
      }
      case ScreenTypeCode.Counter: {
        return AnswerType.Double;
      }
      case ScreenTypeCode.Checkbox: {
        return AnswerType.ListOfLeadContent;
      }
      default: {
        return AnswerType.Unknown;
      }
    }
  }

  public getAnswerTag(question: SurveyModel | SustainabilityQuestionsViewModel, answer: string): string {
    let answerTag: string
    switch (question.ScreenTypeCode) {
      case ScreenTypeCode.YesNo:
      case ScreenTypeCode.RadioButtons:
      case ScreenTypeCode.CardsWithIcons: {
        if (this.isSurveyQuestionType(question)) {
          answerTag = question.RadioButtonValueList.find(x => x.AnswerId.toString() === answer)?.AnswerTag;
        }
        if (!this.isSurveyQuestionType(question)) {
          answerTag = question.AnswerList.find(x => x.AnswerId.toString() === answer)?.AnswerTag;
        }
        break;
      }
      default: {
        answerTag = '';
        break;
      }
    }
    return answerTag
  }

  public getListofAnswerTags(question: SurveyModel | SustainabilityQuestionsViewModel, listofAnswers: (string | number)[]): string {
    const answerTags = new Array<string>();
    if (this.isSurveyQuestionType(question)) {
      question.RadioButtonValueList.forEach(x => {
        listofAnswers.includes(x.AnswerId) && answerTags.push(x.AnswerTag)
      });
    }
    if (!this.isSurveyQuestionType(question)) {
      question.AnswerList.forEach(x => {
        listofAnswers.includes(x.AnswerId) && answerTags.push(x.AnswerTag)
      });
    }
    return answerTags.join(";");
  }

  private isSurveyQuestionType(question: SurveyModel | SustainabilityQuestionsViewModel): question is SurveyModel {
    return (<SurveyModel>question).RadioButtonValueList !== undefined;
  }


  public getAnswerRuleEngineName(question: SurveyModel, answer: string): string {
    let ruleEngineName: string
    switch (question.ScreenTypeCode) {
      case ScreenTypeCode.YesNo:
      case ScreenTypeCode.RadioButtons:
      case ScreenTypeCode.CardsWithIcons: {
        ruleEngineName = question.RadioButtonValueList.find(x => x.AnswerId.toString() === answer).RuleEngineNameAnswer;
        break;
      }
      default: {
        ruleEngineName = '';
        break;
      }
    }
    return ruleEngineName;
  }

  restoreSurveySessionValues(simulationId: number, lastStepSaved?: number): Observable<SimulationSessionData> {
    return this.getSimulationSessionData(Number(simulationId)).pipe(tap(res => {
      this.helper.createSession('userSurvey', btoa(
        JSON.stringify({
          surveyId: res.SurveyId,
          simulationId,
          startingStep: lastStepSaved ? lastStepSaved : null,
          partnerId: res.PartnershipId,
          partnerCodeRange: res.ProductRangeCode,
          surveyCode: res.SurveyId
        })
      )
      );
      this.helper.createSession('userKnowledgeIds', btoa(
        JSON.stringify({
          branchId: res.BranchId,
          scenarioId: res.ScenarioId
        })
      )
      );
      this.helper.createSession('experience', String(res.Experience));
      if (res.SelectedProductTypeCode) {
        this.helper.createSession('selectedProductTypeCode', res.SelectedProductTypeCode);
      } else {
        this.helper.clearSession("selectedProductTypeCode");
      }
      this.helper.createSession('financialInputs', btoa(
        JSON.stringify({
          duration: res.Duration,
          oneShotAmount: res.OneShotAmount,
          monthlyAmount: res.MonthlyAmount
        })
      )
      );
      this.helper.createSession('financialConstants', btoa(
        JSON.stringify({
          minOneShotPremium: res.MinOneShotPremium,
          maxOneShotPremium: res.MaxOneShotPremium,
          minRecurrentPremium: res.MinRecurrentPremium,
          maxRecurrentPremium: res.MaxRecurrentPremium
        })
      )
      );
      const data: BeneficiaryChild = {
        Relationship: Number(res.BeneficiaryChild?.Relationship),
        FirstName: res.BeneficiaryChild?.FirstName,
        LastName: res.BeneficiaryChild?.LastName,
        DateOfBirth: new Date(res.BeneficiaryChild?.DateOfBirth).toDateString(),
        SubscriberId: Number(res.BeneficiaryChild?.SubscriberId)
      }
      this.session.setBeneficiaryChild(data);
    })
    );
  }

  getAdviceSummary(surveyId: number, quoteDetailsId: number, branchId: number): Observable<AdviceSummaryModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/survey/${surveyId}/quoteDetails/${quoteDetailsId}/branch/${branchId}/getadvicesummary`;
    return this.factoryhelperService.get<{ Result: AdviceSummaryModel }>(url).pipe(map(x => x.Result));
  }

  getSimulationSessionData(simulationId: number): Observable<SimulationSessionData> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/getsimulationsessiondata/${simulationId}`;
    return this.factoryhelperService.get<{ Result: SimulationSessionData }>(url).pipe(map(x => x.Result));
  }

  checkFinancialCapping(simulationId: number): Observable<CappingModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${simulationId}/budgetmaxcalculation`;
    return this.factoryhelperService.post<{ Result: CappingModel }>(url, null).pipe(map(x => x.Result));
  }

  saveTaxableIncome(answerModel: SurveyAnswerModel, ruleEngineName?: string): Observable<CappingModel> {
    const culture = this.translateService.currentLang;
    const apiParams = new HttpParams().set('isRuleEngine', `${ruleEngineName ? true : false}`);
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${answerModel.SimulationId}/question/${answerModel.LeadQuestionId}/savebudgetanswers`;
    return this.factoryhelperService.put<{ Result: CappingModel }>(url, answerModel, { params: apiParams }).pipe(
      map(x => x.Result),
      retry(2)
    );
  }

  saveCatchPremiumAmount(data: { CatchPermimumAmount: number, NbrOfMonths: number }, simulationid: number, questionid: number): Observable<boolean> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${simulationid}/question/${questionid}/savecatchpremiumamount`;
    return this.factoryhelperService.put<{ Result: boolean }>(url, data).pipe(map(x => x.Result));
  }

  setCappingModel(cappingModel: CappingModel): void {
    this.cappingModel = cappingModel;
  }
  getCappingModel(): CappingModel {
    return this.cappingModel;
  }
  getCatchUpPremiumData(simulationId: number): Observable<CatchUpPremium> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/simulation/${simulationId}/getcatchuppremiumamount`;
    return this.factoryhelperService.get<{ Result: CatchUpPremium }>(url).pipe(map(x => x.Result));
  }

  getAllProduct(queryObject: unknown): Observable<productDetail[]> {
    const culture = this.translateService.currentLang;
    const queryString = Object.keys(queryObject).map(key => key + '=' + queryObject[key]).join('&');
    const productDetailsUrl = `${this.apiUrl}/${culture}/silruleengine/calcsilprojection?${queryString}`;
    return this.factoryhelperService.get<{ Result: { SimulationList: productDetail[] } }>(productDetailsUrl).pipe(map(x => x?.Result?.SimulationList));
  }

  getproductknowledgedetails(surveyId: number, simulationId: number, sectionId: number): Observable<KnowledgeCheckModel> {
    const culture = this.translateService.currentLang;
    const url = `${this.apiUrl}/${culture}/sildecisiontree/person/${localStorage.personId}/survey/${surveyId}/lastsection/${sectionId}/simulation/${simulationId}/productknowledgedetails`
    return this.factoryhelperService.get<{ Result: KnowledgeCheckModel }>(url).pipe(map(x => x?.Result));
  }

  public getSurveyParams(): SurveyParams {
    const params = sessionStorage.getItem('userSurvey');

    if (params) {
      return JSON.parse(atob(params)) as SurveyParams;
    }

    return {} as SurveyParams;
  }

  public storeSurveyParams(params: SurveyParams): void {
    sessionStorage.setItem('userSurvey', btoa(JSON.stringify(params)));
  }

  initiateprojectionTool(reqObj: InitiateprojectionToolReqObj) {
    const culture = this.translateService.currentLang;
    let url = `${this.projectionToolUrl}/?lang=${culture}&distcha=${reqObj.distcha}&partcdRange=${reqObj.partcdRange}&partcd=${reqObj.partcd}`;
    if (reqObj.ProductFormulaCd) {
      url += `&ProductCd=${reqObj.ProductCd}&ProductFormulaCd=${reqObj.ProductFormulaCd}`;
    }
    return url;
  }
}

